import React, { useMemo, useState } from 'react';
import styles from 'js/components/data_sources/add_ds_form/ConfigureTemplatizedScript.module.scss';
import { AccordionDetails } from '@mui/material';
import AuthenticateDataSource from 'js/components/data_sources/add_ds_form/AuthenticateDataSource';
import vendors from 'js/reducers/vendors';
import { styled } from '@mui/system';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import ArrowIcon from '@mui/icons-material/ArrowForwardIosSharp';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import usePaging from 'new/state/paging/repository/usePaging';
import useCredentials from 'new/state/credentials/repository/useCredentials';
import useCredentialsAdapter from 'new/state/credentials/adapter/useCredentialsAdapter';
import { useIsLoading } from 'new/hooks/adapters/useIsLoading';
import { GET_ALL_CREDENTIALS_REQUEST } from 'js/constants/actionTypes';
import useVendorsAdapter from 'new/state/vendors/adapter/useVendorsAdapter';
import { getEmptyRequiredFields } from 'js/lib/ValidationUtils';
import _ from 'lodash';
import useRequests from 'new/state/request/repository/useRequests';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square children={null} {...props} />
))(({ theme }) => ({
  border: `1px solid ${theme.palette.divider}`,
  '&:not(:last-child)': { borderBottom: 0 },
  '&::before': { display: 'none' },
}));

const Icon = <ArrowIcon sx={{ fontSize: '1rem' }} />;
const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary expandIcon={Icon} {...props} />
))(() => {
  return {
    padding: 0,
    paddingLeft: 8,
    border: 'none',
    borderRadius: 11,
    backgroundColor: 'var(--color-primary-50)',
    flexDirection: 'row-reverse',
    '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
      transform: 'rotate(90deg)',
    },
    '& .MuiAccordionSummary-content': {
      marginLeft: 12,
    },
  };
});

interface CredentialFieldProps {
  onChange: (name: string, value: number) => void;
  values: { [key: string]: string };
  field: {
    name: string;
    fieldName: string;
    display_name: string;
    description: string;
    type: string;
    credential_type: string;
    required: boolean;
    value: number;
  };
}

export const CredentialField = (props: CredentialFieldProps) => {
  const { field, onChange, values } = props;
  const { fieldName, description, display_name, credential_type } = field;

  const { getAuthFields, paging } = usePaging();
  const { loadCredentials, createCredential } = useCredentialsAdapter();
  const { requestFullVendor } = useVendorsAdapter();
  const { credentials, credentialsById, credentialsByType } = useCredentials();
  const isLoadingCredentials = useIsLoading(GET_ALL_CREDENTIALS_REQUEST);

  const { credentialsAllowed, metaInfoById, connAuthById, connAuthMeta } = paging;

  const { templates, items } = getAuthFields(credential_type);

  const [selectedTemplate, setSelectedTemplate] = useState(templates?.[0]?.id);

  const onChangeTemplate = (id) => setSelectedTemplate(Number(id));

  const selectedCredentialId = values?.[fieldName];
  const selectedCredential = selectedCredentialId && credentials[selectedCredentialId];

  const [credential, setCredential] = useState(selectedCredential);
  const [isNewCredential, setNewCredential] = useState(false);

  const { showError } = useRequests();

  const connectionAuthFields = useMemo(() => {
    const template = templates?.find((t) => t.id === selectedTemplate) || templates?.[0];
    return templates && templates?.length > 1 && template
      ? items.filter((item) => template.auth_parameters.includes(item.param_id))
      : items;
  }, [templates, items, selectedTemplate]);

  const connectionAuthMeta = credential_type ? connAuthMeta[credential_type] : null;

  const onChangeApiType = (connector) => {
    requestFullVendor(connector);
  };

  const onCredSelected = (data, isNotNew) => {
    if (!isNotNew) {
      setNewCredential(true);
    } else {
      setNewCredential(false);
      setCredential(credentials[data.id]);
      onChange(fieldName, data.id);
    }
  };

  const onCredInputChange = ({ isNewCredential, selectedCredentialId, selectedCredentialInfo }) => {
    if (isNewCredential !== undefined) {
      setNewCredential(isNewCredential);
    }
    if (selectedCredentialId !== undefined) {
      onChange(fieldName, selectedCredentialId);
    }
    if (selectedCredentialInfo !== undefined) {
      setCredential(selectedCredentialInfo);
    }
  };

  const onSave = async () => {
    if (!isNewCredential) return;
    const { templates, items } =
      connAuthById[(credential && credential._vendorConnectionType) || credential_type];

    const template = templates?.find((t) => t.id === selectedTemplate) || templates?.[0];
    const connectionAuthFields =
      templates && templates?.length > 1 && template
        ? items.filter((item) => template.auth_parameters.includes(item.param_id))
        : items;

    const missingFields = getEmptyRequiredFields(connectionAuthFields, credential);

    if (!_.get(credential, 'cred_name')) {
      missingFields.unshift({
        name: 'cred_name',
        display_name: 'Credential Name',
      });
    }

    if (!_.isEmpty(missingFields)) {
      let error = _.reduce(
        missingFields,
        (message, field) => `${message} ${field.display_name},`,
        'Please enter the following required fields: ',
      );
      error = error.substring(0, error.length - 1);
      showError(error);
      return;
    }

    if (isNewCredential) {
      const response = await createCredential(credential_type, credential);
      const { result, entities } = response;
      setNewCredential(false);
      setCredential(entities.credentials[result]);
      onChange(fieldName, result);
    }
  };

  return (
    <div className="accordion-group" style={{ width: '100%' }}>
      <Accordion defaultExpanded>
        <AccordionSummary className={styles.color_svg}>
          <div className={styles.header_accordion}>
            <h2 className={styles.accordion_title}>{display_name}</h2>
            <span className="accordion-content-grow iteration-info">
              {selectedCredential && selectedCredential.name}
            </span>
          </div>
        </AccordionSummary>
        <AccordionDetails className="accordion-body">
          <div className="accordion-body-content">
            {!!description && <p className="credential-field-description">{description}</p>}
            <AuthenticateDataSource
              onChangeApiType={onChangeApiType}
              formData={{
                selectedCredentialId,
                selectedCredentialInfo: credential,
                isNewCredential,
              }}
              connectionTypes={credentialsAllowed}
              connectionMetas={metaInfoById}
              vendors={vendors}
              selectedConnection={credential_type}
              templates={templates}
              selectedTemplate={selectedTemplate}
              onChangeTemplate={onChangeTemplate}
              connectionAuthFields={connectionAuthFields}
              connectionAuthMeta={connectionAuthMeta}
              dataCredentials={credentialsByType}
              credentialsById={credentialsById}
              isFormAwaitingValidation={false}
              isLoadingData={isLoadingCredentials}
              onRefreshClicked={loadCredentials}
              onFormInputChanged={onCredInputChange}
              onCredSelectionChanged={onCredSelected}
              nextFormStage={onSave}
            />
          </div>
        </AccordionDetails>
      </Accordion>
    </div>
  );
};
